/**
 * interior_plans.ts — PURE purpose furnishing plans (2026-09-22, the
 * interiors research programme's first slice: "give every building a
 * recognisable purpose within the current shell").
 *
 * A plan is a list of SLOTS in building-local metres: x across the room,
 * z toward the back wall (+Z); the door is on the −Z wall, the stairs climb
 * the −X (west) wall (building_interiors.ts). YAW convention (the hive/guild
 * plans'): 0 faces +Z (away from the door), π faces the door, π/2 faces +X,
 * −π/2 faces −X — so a counter on the back wall is `π`, a shelf on the +X
 * wall is `−π/2`.
 *
 * Every plan goes through `fitPlan`: unknown catalog keys drop; a slot's
 * CENTRE must clear the wall margin, the door corridor and the stair box (the
 * guild hall's `clearOf`, shared) and its DRAWN footprint must clear the
 * walls, the doorway and the stair column + rail (`footprintClear`) — a slot
 * that fails is nudged the least that clears it (`settleSlot`, ≤ 1.6 m) or
 * dropped; and a slot whose drawn footprint sits on top of an accepted one
 * drops — measured at FURNITURE_RENDER_SCALE, because the catalog's unscaled
 * dims undercount what is actually on the floor (§3.3 of the programme).
 * Author the important piece first: first come, first placed.
 *
 * Floors: 0 is the purpose floor; floors ≥ 1 get the purpose's UPPER plan
 * (a ward, a lounge, a stock room, offices, or plain quarters). Deeds and
 * markers only — settlement homes keep `furnishVersonaHome` (its coordinates
 * are mirrored by home_layout.ts for the residents: THE MIRROR LAW).
 */
import { FURNITURE } from './furniture_catalog.js';
import { FURNITURE_RENDER_SCALE } from './furniture_place.js';
import type { InteriorPurpose } from './interior_purpose.js';

export interface PlanSlot { key: string; x: number; z: number; yaw: number }
/** The room frame a plan is fitted to: interiorSpec's hw/hd/stair (`w`
 *  defaults to the shell's 1.6 m run). */
export interface PlanFrame { hw: number; hd: number; stair: { x: number; z: number; d: number; w?: number } }

const HP = Math.PI / 2;
/** Two pieces whose centres are closer than this fraction of their summed
 *  drawn radii are ON TOP of each other (a chair beside its table is ~0.6). */
export const OVERLAP_K = 0.55;
/** Rugs go UNDER things by design. */
const UNDERLAY = new Set(['rug_woven']);

/** How far a DRAWN footprint may sink into a wall: the visual band is 0.16 m
 *  centred on the room edge and the building's own exterior stands the shell
 *  inset (0.45 m) beyond it, so a bed's head in the band reads as "against
 *  the wall" from inside and is never seen from outside. */
export const WALL_SINK = 0.15;
/** Half the doorway a drawn footprint must leave clear for the first 3.4 m:
 *  the 1.9 m opening plus a shoulder. (The CENTRE rule keeps its 1.4 m band.) */
export const DOOR_BOX_HALF = 1.2;
/** A nudge never carries a piece farther than this from where it was authored. */
export const MAX_NUDGE_M = 1.6;
/** The stair column's +X face: the 0.4 m guard rail at `x + w/2 + 0.21`
 *  (building_interiors.ts), so its outer face is `x + w/2 + 0.41`. */
export function stairEdgeX(f: PlanFrame): number {
  return f.stair.x + (f.stair.w ?? 1.6) / 2 + 0.41;
}

/** Is a slot clear of the wall margin, the door corridor and the stair box? */
export function slotClear(f: PlanFrame, x: number, z: number, margin = 0.55): boolean {
  if (Math.abs(x) < 1.4 && z < -f.hd + 3.4) return false;                                   // door corridor
  if (x > f.stair.x - 2.0 && x < f.stair.x + 2.0 && z > f.stair.z - f.stair.d / 2 - 1.8 && z < f.stair.z + f.stair.d / 2 + 1.8) return false; // stairs
  return Math.abs(x) < f.hw - margin && Math.abs(z) < f.hd - margin;
}

/** Footprint radius of a catalog piece AS DRAWN (render scale). */
export function slotRadius(key: string): number {
  const s = FURNITURE[key];
  return s ? (Math.max(s.w, s.d) * FURNITURE_RENDER_SCALE) / 2 : 0.5;
}

/** A piece's DRAWN half-extents on the room axes — width along x at yaw 0,
 *  the axis-aligned box of the turned footprint (render scale; the fitted
 *  model is uniformly scaled INSIDE w×d, so this is an upper bound). */
export function drawnHalf(key: string, yaw: number): { hx: number; hz: number } {
  const s = FURNITURE[key];
  if (!s) return { hx: 0.5, hz: 0.5 };
  const c = Math.abs(Math.cos(yaw)), n = Math.abs(Math.sin(yaw));
  const w = (s.w * FURNITURE_RENDER_SCALE) / 2, d = (s.d * FURNITURE_RENDER_SCALE) / 2;
  return { hx: c * w + n * d, hz: n * w + c * d };
}

/** Does a piece's DRAWN footprint keep out of the walls (past the sink), the
 *  doorway and the stair column with its rail? `slotClear` judges the CENTRE
 *  (the authored clearances); this judges what is actually on the floor —
 *  the centre-only test let a 2.7 m table reach through the rail and onto the
 *  treads, a 3 m sofa cross the stairs, a counter stand in the doorway
 *  (bug sweep 2026-09-23). A rug may lie in the doorway, never over the
 *  stairwell (upstairs that is the slab opening — it would float). */
export function footprintClear(f: PlanFrame, key: string, x: number, z: number, yaw: number): boolean {
  const { hx, hz } = drawnHalf(key, yaw);
  if (Math.abs(x) + hx > f.hw + WALL_SINK + 1e-9 || Math.abs(z) + hz > f.hd + WALL_SINK + 1e-9) return false;
  if (!UNDERLAY.has(key) && x - hx < DOOR_BOX_HALF && x + hx > -DOOR_BOX_HALF && z - hz < -f.hd + 3.4) return false;
  const z0 = f.stair.z - f.stair.d / 2 - 1.2, z1 = f.stair.z + f.stair.d / 2 + 0.9; // the approach · the run · the landing lip
  if (x - hx < stairEdgeX(f) && z + hz > z0 && z - hz < z1) return false;
  return true;
}

/** Seat a slot: where it was authored, else NUDGED the least that clears its
 *  drawn footprint — pulled in from a wall, slid east of the stair rail, or
 *  aside of the doorway — never farther than MAX_NUDGE_M. null = it does not
 *  fit this frame (the plan drops it, as it always dropped an off-frame slot). */
export function settleSlot(f: PlanFrame, s: PlanSlot): PlanSlot | null {
  const ok = (x: number, z: number): boolean =>
    Math.hypot(x - s.x, z - s.z) <= MAX_NUDGE_M && slotClear(f, x, z) && footprintClear(f, s.key, x, z, s.yaw);
  if (ok(s.x, s.z)) return s;
  const { hx, hz } = drawnHalf(s.key, s.yaw);
  const xMax = Math.max(0, f.hw + WALL_SINK - hx), zMax = Math.max(0, f.hd + WALL_SINK - hz);
  const cx = Math.max(-xMax, Math.min(xMax, s.x)), cz = Math.max(-zMax, Math.min(zMax, s.z));
  const east = Math.max(cx, stairEdgeX(f) + 0.05 + hx);
  const aside = (cx >= 0 ? 1 : -1) * (DOOR_BOX_HALF + 0.05 + hx);
  for (const [x, z] of [[cx, cz], [east, cz], [aside, cz]] as const) {
    if (ok(x, z)) return { ...s, x, z };
  }
  return null;
}

/** Fit a hand-authored plan to a frame (see the header). */
export function fitPlan(f: PlanFrame, slots: readonly PlanSlot[]): PlanSlot[] {
  const out: PlanSlot[] = [];
  for (const authored of slots) {
    if (!FURNITURE[authored.key]) continue;
    const s = settleSlot(f, authored);
    if (!s) continue;
    if (!UNDERLAY.has(s.key)) {
      const r = slotRadius(s.key);
      let hit = false;
      for (const o of out) {
        if (UNDERLAY.has(o.key)) continue;
        if (Math.hypot(o.x - s.x, o.z - s.z) < (r + slotRadius(o.key)) * OVERLAP_K) { hit = true; break; }
      }
      if (hit) continue;
    }
    out.push(s);
  }
  return out;
}

const S = (key: string, x: number, z: number, yaw: number): PlanSlot => ({ key, x, z, yaw });

/** A table with a chair on each long side (the chair on the door side faces the table). */
const tableFor2 = (x: number, z: number): PlanSlot[] => [S('table_dining', x, z, 0), S('chair_dining', x, z - 1.1, 0), S('chair_dining', x, z + 1.1, Math.PI)];

/** Floor 0 — the purpose floor. */
function groundPlan(p: InteriorPurpose, hw: number, hd: number): PlanSlot[] {
  switch (p) {
    case 'hospitality': return [ // the bar along the back wall, tables in the room, the stage cue by the window
      S('counter_bar', -hw * 0.25, hd - 1.15, Math.PI), S('counter_bar', -hw * 0.25 + 2.4, hd - 1.15, Math.PI),
      S('kitchen_unit', hw * 0.45, hd - 1.1, Math.PI), S('cooler_unit', hw * 0.45 + 1.6, hd - 1.0, Math.PI),
      S('stool_shop', -hw * 0.25 - 1.0, hd - 2.4, 0), S('stool_shop', -hw * 0.25, hd - 2.4, 0), S('stool_shop', -hw * 0.25 + 1.0, hd - 2.4, 0),
      ...tableFor2(-hw * 0.45, -hd * 0.15), ...tableFor2(hw * 0.35, -hd * 0.15),
      S('table_low', 0, hd * 0.25, 0), S('sofa_low', 0, hd * 0.25 + 1.2, Math.PI), S('armchair_wing', -1.6, hd * 0.25, HP),
      S('rug_woven', 0, -hd * 0.15, 0),
      S('screen_holo', hw - 1.1, -hd + 2.2, -Math.PI / 4), S('light_column', hw - 0.9, hd * 0.5, 0), S('light_column', -hw + 1.0, hd - 1.3, 0),
      S('plant_large', hw - 1.0, -hd + 1.2, 0), S('bench_hall', 2.6, -hd + 1.1, 0),
    ];
    case 'retail': return [ // checkout by the door, shelving on the walls, display islands
      S('counter_bar', hw * 0.55, -hd + 2.6, 0), S('stool_shop', hw * 0.55, -hd + 1.5, 0), S('table_side', hw * 0.55 + 1.5, -hd + 2.6, 0),
      S('shelf_unit', hw - 0.75, -hd * 0.4, -HP), S('shelf_unit', hw - 0.75, -hd * 0.4 + 2.2, -HP), S('shelf_unit', hw - 0.75, -hd * 0.4 + 4.4, -HP),
      S('shelf_unit', -hw * 0.3, hd - 0.7, Math.PI), S('shelf_unit', hw * 0.3, hd - 0.7, Math.PI),
      S('cabinet_low', -hw * 0.35, -hd * 0.1, 0), S('cabinet_low', -hw * 0.35, hd * 0.35, 0),
      S('screen_holo', 0, hd - 1.3, Math.PI), S('rug_woven', 0, 0, 0),
      S('lamp_floor', -hw + 1.0, -hd + 1.2, Math.PI / 4), S('plant_small', 1.9, -hd + 0.9, 0),
    ];
    case 'workshop': return [ // benches down the +X wall, fabricators at the back, the order desk by the door
      S('bench_work', hw - 0.85, -hd * 0.35, -HP), S('bench_work', hw - 0.85, 0, -HP), S('bench_work', hw - 0.85, hd * 0.35, -HP),
      S('fabricator_pod', -hw * 0.25, hd - 0.85, Math.PI), S('fabricator_pod', hw * 0.25, hd - 0.85, Math.PI),
      S('shelf_unit', -hw * 0.55, hd - 0.7, Math.PI), S('shelf_unit', hw * 0.6, hd - 0.7, Math.PI),
      S('desk_terminal', -hw * 0.4, -hd + 2.4, 0), S('stool_shop', -hw * 0.4, -hd + 1.5, 0),
      S('chest_storage', 2.2, -hd + 0.9, 0), S('chest_storage', 3.2, -hd + 0.9, 0),
      S('cabinet_low', 0, hd * 0.3, 0), S('stool_shop', hw - 2.0, 0, HP),
      S('light_column', hw - 0.9, hd - 1.2, 0), S('light_column', -2.4, -hd + 1.0, 0),
    ];
    case 'clinic': return [ // reception by the door, the waiting row, treatment beds down the +X wall, supplies at the back
      S('desk_terminal', hw * 0.4, -hd + 2.6, 0), S('chair_dining', hw * 0.4, -hd + 1.7, 0), S('table_side', hw * 0.4 + 1.4, -hd + 2.6, 0),
      S('chair_dining', -hw * 0.45, -hd + 1.0, 0), S('chair_dining', -hw * 0.45 + 0.9, -hd + 1.0, 0), S('chair_dining', -hw * 0.45 + 1.8, -hd + 1.0, 0), S('chair_dining', -hw * 0.45 + 2.7, -hd + 1.0, 0),
      S('bed_single', hw - 1.35, -hd * 0.4, -HP), S('bed_single', hw - 1.35, -hd * 0.4 + 2.2, -HP), S('bed_single', hw - 1.35, -hd * 0.4 + 4.4, -HP), S('bed_single', hw - 1.35, -hd * 0.4 + 6.6, -HP),
      S('cabinet_low', -hw * 0.3, hd - 0.7, Math.PI), S('cabinet_low', 0, hd - 0.7, Math.PI), S('cooler_unit', hw * 0.3, hd - 0.75, Math.PI), S('wardrobe_tall', -hw * 0.6, hd - 0.7, Math.PI),
      S('screen_holo', -1.9, hd - 1.9, Math.PI), S('light_column', hw - 0.9, hd - 1.1, 0), S('light_column', -hw * 0.5, -hd * 0.1, 0),
      S('plant_small', 1.9, -hd + 0.9, 0), S('rug_woven', 0, 0, 0),
    ];
    case 'bank': return [ // the teller line across the room, lockers behind it, the queue benches and a consultation desk
      S('counter_bar', -hw * 0.35, hd * 0.15, Math.PI), S('counter_bar', 0, hd * 0.15, Math.PI), S('counter_bar', hw * 0.35, hd * 0.15, Math.PI),
      S('stool_shop', -hw * 0.35, hd * 0.15 + 1.0, Math.PI), S('stool_shop', 0, hd * 0.15 + 1.0, Math.PI), S('stool_shop', hw * 0.35, hd * 0.15 + 1.0, Math.PI),
      S('cabinet_low', -hw * 0.4, hd - 0.7, Math.PI), S('cabinet_low', 0, hd - 0.7, Math.PI), S('cabinet_low', hw * 0.4, hd - 0.7, Math.PI),
      S('desk_writing', hw - 1.2, -hd * 0.35, -HP), S('chair_dining', hw - 2.2, -hd * 0.35, HP),
      S('bench_hall', -hw * 0.35, -hd * 0.45, 0), S('bench_hall', hw * 0.35, -hd * 0.45, 0),
      S('screen_holo', 2.6, -hd + 1.1, 0), S('light_column', hw - 0.9, hd * 0.55, 0), S('light_column', -hw + 1.0, hd - 1.2, 0),
      S('rug_woven', 0, -hd * 0.2, 0), S('plant_large', -2.4, -hd + 1.1, 0),
    ];
    case 'garage': return [ // service benches on +X, parts at the back, the work-order desk by the door
      S('bench_work', hw - 0.85, -hd * 0.2, -HP), S('bench_work', hw - 0.85, hd * 0.25, -HP),
      S('fabricator_pod', hw * 0.3, hd - 0.85, Math.PI), S('shelf_unit', -hw * 0.3, hd - 0.7, Math.PI), S('shelf_unit', hw * 0.65, hd - 0.7, Math.PI),
      S('chest_storage', -hw * 0.55, hd - 0.9, Math.PI), S('chest_storage', -hw * 0.55 + 1.0, hd - 0.9, Math.PI), S('chest_storage', -hw * 0.55 + 2.0, hd - 0.9, Math.PI),
      S('desk_terminal', -hw * 0.4, -hd + 2.5, 0), S('stool_shop', -hw * 0.4, -hd + 1.6, 0),
      S('cabinet_low', 0, 0, 0), S('light_column', hw - 0.9, hd - 1.2, 0), S('light_column', -2.4, -hd + 1.0, 0), S('plant_small', 2.0, -hd + 0.9, 0),
    ];
    case 'civic': return [ // clerks' desks, the council table, public benches, the records wall
      S('table_dining', 0, hd * 0.35, HP), S('chair_dining', -1.2, hd * 0.35, HP), S('chair_dining', 1.2, hd * 0.35, -HP), S('chair_dining', 0, hd * 0.35 - 1.1, 0), S('chair_dining', 0, hd * 0.35 + 1.1, Math.PI),
      S('desk_terminal', -hw * 0.4, -hd * 0.15, 0), S('chair_dining', -hw * 0.4, -hd * 0.15 + 1.0, Math.PI), S('desk_terminal', hw * 0.4, -hd * 0.15, 0), S('chair_dining', hw * 0.4, -hd * 0.15 + 1.0, Math.PI),
      S('bench_hall', -2.2, -hd + 1.1, 0), S('bench_hall', 2.2, -hd + 1.1, 0),
      S('bookcase_arch', -hw * 0.35, hd - 0.7, Math.PI), S('bookcase_arch', hw * 0.35, hd - 0.7, Math.PI), S('screen_holo', 0, hd - 1.2, Math.PI),
      S('rug_woven', 0, hd * 0.35, 0), S('light_column', hw - 0.9, hd - 1.1, 0), S('light_column', hw - 0.9, -hd + 1.1, 0),
      S('statue_small', -2.3, -hd + 2.3, 0), S('statue_small', 2.3, -hd + 2.3, 0), S('plant_large', hw - 1.0, 0, 0),
    ];
    case 'theater': return [ // the stage at the back, bench rows facing it, concessions by the door
      S('rug_woven', 0, hd - 2.2, HP), S('screen_holo', -2.2, hd - 1.0, Math.PI), S('screen_holo', 2.2, hd - 1.0, Math.PI),
      S('light_column', -hw * 0.6, hd - 1.2, 0), S('light_column', hw * 0.6, hd - 1.2, 0),
      ...[0, 1, 2, 3].flatMap((r) => [S('bench_hall', -2.0, hd * 0.3 - r * 1.6, 0), S('bench_hall', 2.0, hd * 0.3 - r * 1.6, 0)]),
      S('counter_bar', hw - 1.2, -hd + 2.6, -HP), S('plant_large', -2.4, -hd + 1.1, 0), S('lamp_floor', hw - 0.9, -hd + 1.2, -Math.PI / 4),
    ];
    case 'transit': return [ // the waiting benches, ticketing and the departure boards at the back, luggage on +X
      S('bench_hall', -hw * 0.35, -hd * 0.3, 0), S('bench_hall', hw * 0.35, -hd * 0.3, 0), S('bench_hall', -hw * 0.35, -hd * 0.3 + 1.4, 0), S('bench_hall', hw * 0.35, -hd * 0.3 + 1.4, 0),
      S('desk_terminal', -hw * 0.3, hd - 1.0, Math.PI), S('desk_terminal', hw * 0.3, hd - 1.0, Math.PI),
      S('screen_holo', -hw * 0.6, hd - 1.1, Math.PI), S('screen_holo', hw * 0.6, hd - 1.1, Math.PI),
      S('cabinet_low', hw - 1.0, 0, -HP), S('chest_storage', hw - 0.8, hd * 0.35, -HP), S('chest_storage', hw - 0.8, hd * 0.35 + 1.0, -HP),
      S('light_column', hw - 0.9, hd - 1.2, 0), S('light_column', -hw + 1.0, hd - 1.3, 0), S('plant_large', 2.4, -hd + 1.1, 0), S('rug_woven', 0, hd * 0.25, 0),
    ];
    case 'residence': case 'lodge': case 'shed': return [ // a lived-in ground floor: sitting, cooking, eating, sleeping
      S('table_low', 0, hd * 0.2, 0), S('sofa_low', 0, hd * 0.2 + 1.2, Math.PI), S('armchair_wing', -1.7, hd * 0.2, HP), S('rug_woven', 0, hd * 0.2, 0),
      S('kitchen_unit', hw * 0.45, hd - 0.85, Math.PI), S('cooler_unit', hw * 0.45 + 1.6, hd - 0.85, Math.PI),
      ...tableFor2(hw * 0.45, hd * 0.1),
      S('bed_double', -hw * 0.45, hd - 1.6, Math.PI), S('wardrobe_tall', -hw + 0.85, hd - 1.0, HP),
      S('bookcase_arch', hw - 0.7, 0, -HP), S('stove_hearth', hw - 0.9, hd * 0.5, -HP),
      S('lamp_floor', hw - 0.9, -hd + 1.2, -Math.PI / 4), S('plant_small', 2.0, -hd + 0.9, 0), S('chest_storage', -2.3, -hd + 0.9, 0), S('shelf_unit', -hw * 0.35, -hd + 0.65, 0),
    ];
    case 'office': case 'apartment': return [ // desks facing the door, a waiting sofa, the records wall
      S('desk_terminal', -hw * 0.4, -hd * 0.2, Math.PI), S('chair_dining', -hw * 0.4, -hd * 0.2 + 1.0, Math.PI),
      S('desk_terminal', 0, -hd * 0.2, Math.PI), S('chair_dining', 0, -hd * 0.2 + 1.0, Math.PI),
      S('desk_terminal', hw * 0.4, -hd * 0.2, Math.PI), S('chair_dining', hw * 0.4, -hd * 0.2 + 1.0, Math.PI),
      S('desk_writing', hw * 0.4, hd * 0.4, 0), S('chair_dining', hw * 0.4, hd * 0.4 - 1.0, 0),
      S('cabinet_low', -hw * 0.35, hd - 0.7, Math.PI), S('cabinet_low', hw * 0.35, hd - 0.7, Math.PI), S('bookcase_arch', hw - 0.7, hd * 0.1, -HP), S('screen_holo', 0, hd - 1.2, Math.PI),
      S('sofa_low', -hw * 0.4, hd * 0.4, 0), S('rug_woven', 0, hd * 0.3, 0),
      S('plant_large', -2.4, -hd + 1.1, 0), S('lamp_floor', hw - 0.9, -hd + 1.2, -Math.PI / 4),
    ];
    case 'guild': case 'generic': default: return [ // a modest hall: a table, a shelf, a bench by the door
      ...tableFor2(0, hd * 0.15), S('rug_woven', 0, hd * 0.15, 0), S('shelf_unit', hw - 0.6, 0, -HP),
      S('lamp_floor', hw - 0.9, -hd + 1.2, -Math.PI / 4), S('plant_small', 2.0, -hd + 0.9, 0), S('bench_hall', -2.4, -hd + 1.1, 0),
    ];
  }
}

/** Floors ≥ 1 — what the purpose keeps upstairs. */
function upperPlan(p: InteriorPurpose, hw: number, hd: number): PlanSlot[] {
  switch (p) {
    case 'clinic': return [ // the ward: beds down +X and along the back wall
      S('bed_single', hw - 1.35, -hd * 0.4, -HP), S('bed_single', hw - 1.35, -hd * 0.4 + 2.2, -HP), S('bed_single', hw - 1.35, -hd * 0.4 + 4.4, -HP),
      S('bed_single', -hw * 0.4, hd - 1.35, Math.PI), S('bed_single', -hw * 0.4 + 2.0, hd - 1.35, Math.PI), S('bed_single', -hw * 0.4 + 4.0, hd - 1.35, Math.PI),
      S('cabinet_low', hw * 0.4, -hd + 1.0, 0), S('lamp_floor', -hw + 1.0, -hd + 1.2, Math.PI / 4), S('rug_woven', 0, 0, 0), S('plant_small', 2.0, -hd + 0.9, 0),
    ];
    case 'hospitality': case 'theater': case 'lodge': return [ // the lounge / green room
      S('table_low', 0, hd * 0.15, 0), S('sofa_low', 0, hd * 0.15 + 1.2, Math.PI), S('sofa_low', 0, hd * 0.15 - 1.2, 0), S('armchair_wing', -1.7, hd * 0.15, HP),
      S('rug_woven', 0, hd * 0.15, 0), S('cabinet_low', hw * 0.4, hd - 0.7, Math.PI), S('lamp_floor', hw - 0.9, hd - 1.2, 0), S('plant_large', hw - 1.0, -hd + 1.1, 0), S('wardrobe_tall', -hw * 0.4, hd - 0.7, Math.PI),
    ];
    case 'retail': case 'workshop': case 'garage': case 'transit': return [ // the stock room
      S('shelf_unit', hw - 0.6, -hd * 0.3, -HP), S('shelf_unit', hw - 0.6, -hd * 0.3 + 1.6, -HP), S('shelf_unit', hw - 0.6, -hd * 0.3 + 3.2, -HP),
      S('shelf_unit', -hw * 0.3, hd - 0.6, Math.PI), S('shelf_unit', hw * 0.3, hd - 0.6, Math.PI),
      S('chest_storage', -2.2, -hd + 0.9, 0), S('chest_storage', -3.2, -hd + 0.9, 0), S('desk_writing', 2.4, -hd + 1.1, 0), S('lamp_floor', hw - 0.9, hd - 1.2, 0),
    ];
    case 'bank': case 'civic': case 'office': case 'apartment': return [ // offices upstairs
      S('desk_terminal', -hw * 0.35, 0, Math.PI), S('chair_dining', -hw * 0.35, 1.0, Math.PI), S('desk_terminal', hw * 0.35, 0, Math.PI), S('chair_dining', hw * 0.35, 1.0, Math.PI),
      S('bookcase_arch', -hw * 0.3, hd - 0.7, Math.PI), S('bookcase_arch', hw * 0.3, hd - 0.7, Math.PI), S('cabinet_low', hw - 0.9, -hd * 0.4, -HP),
      S('rug_woven', 0, 0, 0), S('lamp_floor', hw - 0.9, hd - 1.2, 0), S('plant_small', 2.0, -hd + 0.9, 0),
    ];
    default: return [ // quarters
      S('bed_single', -hw * 0.4, hd - 1.5, Math.PI), S('bed_single', hw * 0.4, hd - 1.5, Math.PI), S('wardrobe_tall', 0, hd - 0.7, Math.PI),
      S('desk_writing', hw - 0.85, -hd * 0.2, -HP), S('chair_dining', hw - 1.9, -hd * 0.2, HP),
      S('lamp_floor', hw - 0.9, hd * 0.4, 0), S('rug_woven', 0, 0, 0), S('chest_storage', 2.2, -hd + 0.9, 0),
    ];
  }
}

/** The nameplate inside the door (BuildingInteriorModule.placeSign): on the
 *  door wall, right of the opening, `SIGN_W` wide at `SIGN_X` — or narrower
 *  and nearer the door when the +X wall is closer than that (a fixed 2.3 m ±
 *  0.95 plate ran into the side wall of any room under 6.5 m wide). null =
 *  no wall to hang it on. */
export const SIGN_W = 1.9, SIGN_X = 2.3;
export function doorSignSpan(hw: number): { x: number; w: number } | null {
  const lo = DOOR_BOX_HALF, hi = hw - 0.12;          // clear of the door frame · inside the side wall's band
  const w = Math.min(SIGN_W, hi - lo);
  if (!(w >= 0.9)) return null;
  return { x: Math.max(lo + w / 2, Math.min(SIGN_X, hi - w / 2)), w };
}

/** The fitted plan for one floor of a purpose (floors ≥ 1 share the upper plan). */
export function purposePlan(p: InteriorPurpose, f: PlanFrame, floor: number): PlanSlot[] {
  return fitPlan(f, floor === 0 ? groundPlan(p, f.hw, f.hd) : upperPlan(p, f.hw, f.hd));
}

/** The purposes with a plan of their own on the ground floor (the rest share the generic hall). */
export const PLANNED_PURPOSES: ReadonlyArray<InteriorPurpose> = ['hospitality', 'retail', 'workshop', 'clinic', 'bank', 'garage', 'civic', 'theater', 'transit', 'residence', 'office', 'generic'];
